// StoresPanel — the Stores account section: the customer's stores as tiles
// (My Account C1, task T7b; rewritten by the account-area redesign Task 7,
// Amendment 2026-10-02 F8/O9).
//
// ⚠️ AMENDED 2026-10-04 (account-area redesign, Task 7). ORIGINAL: a thin
// loader that delegated everything to ManageStoresPanel's page-swap CRUD.
// CURRENT (the amendment's tile presentation): this panel OWNS the section's
// data (stores + the owned-products read the tiles' product lists come from)
// and the three EmbedDialogs (add / edit / remove); ManageStoresPanel renders
// the tiles and calls back. The loads run under Promise.allSettled — one
// failing source doesn't discard the others (mirrors ProductsPanel's own
// degraded load); a stores failure is the only one that blanks the section,
// so it surfaces as the Retry banner while an owned-products failure still
// renders the tiles that did load (without products).
//
// ⚠️ AMENDED 2026-10-27 (TD-671, Task 9). ORIGINAL: the tiles joined the
// (product, store) associations (GET /me/product-store) against product names
// derived in the browser from the customer's order lines. CURRENT: ONE read,
// GET /me/owned-products (listOwnedProducts), whose one-time items carry
// `assignments` (store + units) and whose subscription items carry `store`;
// the server owns the ownership rules, so the browser derivation is deleted.
// The read needs view-orders (F10): a member without it sends NO owned-products
// request and sees the tiles without products (the TD-583 pattern; an unloaded
// self-view permits everything, the server's refusal being the real control).
//
// Edit re-seed (§59): the dialog is keyed off editingId and StoreForm mounts
// from the LIVE stores state, so every open reads the freshest version — a
// save updates the store (with its incremented version) and the next edit
// sends THAT version in If-Match.
//
// ⚠️ AMENDED 2026-10-06 (account-area redesign, Task 18). Tiles gained a
// second data stage: once the stores resolve, EACH store's newest orders are
// fetched (listStoreOrders — the ownership-checked per-store filter) and
// handed to ManageStoresPanel keyed by store id. The stage is a
// Promise.allSettled across stores — one store's order fetch failing leaves
// that store's ORDERS BLOCK SILENT (no key in ordersByStore = loading or
// failed, never a false "no orders" claim) while every other tile still
// renders its orders. Tile orders load with the section (mount + Retry);
// a store added via the Add dialog has no key until the next full load —
// harmless, because a brand-new store has no orders to show anyway.
//
// ⚠️ AMENDED 2026-10-27 (TD-671 Task 18). ORIGINAL: the per-store orders stage
// ran for every member. CURRENT: it runs only with view-orders (F10, the same
// gate as the owned-products read); without it no GET /me/orders is sent and
// every tile's orders block stays silent. The remove-store dialog now says the
// store's units return to Unassigned (I3), since the per-product "None"
// association it used to describe no longer exists.
//
// Inline styles only — no Tailwind.

import { useState, useEffect, useCallback } from "react";
import { useEmbedTokens, EmbedBanner, EmbedButton, EmbedCard, EmbedDialog, EmbedText } from "../../embed-shared/primitives";
import { ApiError } from "../../embed-shared/api-client";
import { ManageStoresPanel } from "./ManageStoresPanel";
import { StoreForm } from "./StoreForm";
import { listStores, deleteStore, listStoreOrders } from "./store-api";
import type { CustomerStoreWire } from "./store-api";
import { listOwnedProducts } from "./owned-products-api";
import type { OwnedItemWire } from "./owned-products-api";
import { productsAccess } from "./account-sections";
import type { SelfViewWire } from "./membership-api";
import type { OrderSummary } from "../../embed-shared/types";

function messageOf(reason: unknown, fallback: string): string {
  return reason instanceof ApiError ? reason.message : fallback;
}

/** Groups the owned-products read by store id for the tiles. A one-time item
 *  contributes one entry per assignment (with its units); a subscription item
 *  contributes one entry under its store (quantity null), skipped when its
 *  subscription is canceled (the server lists every status; the client
 *  filters) and once per product per store. Without manage-subscriptions
 *  `subscription` is null, so the status is unknown and the item is kept.
 *  Items with no store (hidden, or the store is gone) appear on no tile. */
export function groupProductsByStore(
  items: OwnedItemWire[],
): Record<string, { name: string; quantity: number | null }[]> {
  const out: Record<string, { name: string; quantity: number | null }[]> = {};
  const add = (storeId: string, name: string, quantity: number | null) => {
    (out[storeId] ??= []).push({ name, quantity });
  };
  for (const item of items) {
    if (item.kind === "subscription") {
      const s = item.store;
      if (s && item.subscription?.status !== "canceled" &&
          !(out[s.id] ?? []).some((p) => p.quantity === null && p.name === item.product.name)) {
        add(s.id, item.product.name, null);
      }
    } else {
      for (const a of item.assignments ?? []) add(a.store.id, item.product.name, a.quantity);
    }
  }
  return out;
}

export interface StoresPanelProps {
  /** The shell's loaded self-view; null/undefined = unloaded = everything
   *  permitted (fail-open presentation, as ProductsPanel). */
  selfView?: SelfViewWire | null;
  /** Opens an order from a tile's compact card inside the shell (wired to
   *  the shell's openOrder by AccountCenter). Absent = the card is inert. */
  onOpenOrder?: (orderId: string) => void;
  /** Deep-link to the Orders section filtered to this store (the tile's
   *  View all control; wired to the shell by AccountCenter). */
  onViewStoreOrders?: (store: CustomerStoreWire) => void;
}

export function StoresPanel({ selfView = null, onOpenOrder, onViewStoreOrders }: StoresPanelProps) {
  const t = useEmbedTokens();
  // Keyed on the boolean, not the self-view object (a refresh yields a fresh
  // object with no permission change; see ProductsPanel).
  const canViewOrders = productsAccess(selfView).orders;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [stores, setStores] = useState<CustomerStoreWire[]>([]);
  // undefined = hidden by permissions (no view-orders): tiles show no product line.
  const [productsByStore, setProductsByStore] = useState<
    Record<string, { name: string; quantity: number | null }[]> | undefined
  >({});
  // Task 18: per-store newest orders. A store whose fetch failed has NO key
  // (the tile's orders block stays silent for it) — only SUCCESSFUL fetches
  // get a key, whose (possibly empty) array is the honest "loaded, zero".
  const [ordersByStore, setOrdersByStore] = useState<Record<string, OrderSummary[]>>({});

  // Dialog state: one flag per dialog. Edit is an ID, not a captured store
  // object, so the form always re-seeds from live state (§59 above).
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [removingStore, setRemovingStore] = useState<CustomerStoreWire | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // Task 18's second stage: one filtered fetch per store, allSettled across
  // them. Only resolved fetches land in ordersByStore (a failure leaves its
  // store's key absent — the tile stays silent, never a false "no orders").
  const loadTileOrders = useCallback(async (storeList: CustomerStoreWire[]) => {
    // Without view-orders the per-store orders request is NEVER sent (the same
    // F10 gate as the owned-products read): GET /me/orders is a view-orders
    // route, so the server would refuse it. No key lands in ordersByStore, so
    // every tile's orders block stays silent.
    if (!canViewOrders || storeList.length === 0) {
      setOrdersByStore({});
      return;
    }
    const results = await Promise.allSettled(
      storeList.map(async (s) => [s.id, await listStoreOrders(s.id)] as const),
    );
    const next: Record<string, OrderSummary[]> = {};
    for (const r of results) {
      if (r.status === "fulfilled") {
        const [id, orders] = r.value;
        next[id] = orders;
      }
    }
    setOrdersByStore(next);
  }, [canViewOrders]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    // F10: without view-orders the owned-products read is NEVER sent.
    const [storesR, ownedR] = await Promise.allSettled([
      listStores(),
      canViewOrders ? listOwnedProducts() : Promise.resolve([] as OwnedItemWire[]),
    ]);

    const failures: string[] = [];
    let loadedStores: CustomerStoreWire[] = [];
    if (storesR.status === "fulfilled") {
      loadedStores = storesR.value;
      setStores(storesR.value);
    } else failures.push(messageOf(storesR.reason, "Failed to load your stores"));
    if (!canViewOrders) setProductsByStore(undefined);
    else if (ownedR.status === "fulfilled") setProductsByStore(groupProductsByStore(ownedR.value));
    else {
      setProductsByStore({});
      failures.push(messageOf(ownedR.reason, "Failed to load your products"));
    }

    setLoadError(failures.length > 0 ? failures.join(" ") : null);
    setLoading(false);

    // Second stage (Task 18): only when the stores themselves resolved —
    // no ids, nothing to fetch. Cleared first so a retry that lost stores
    // doesn't show stale tiles' orders.
    if (storesR.status === "fulfilled") {
      await loadTileOrders(loadedStores);
    } else {
      setOrdersByStore({});
    }
  }, [loadTileOrders, canViewOrders]);

  useEffect(() => {
    void load();
  }, [load]);

  const editingStore = editingId != null ? (stores.find((s) => s.id === editingId) ?? null) : null;

  const handleRemove = useCallback(async () => {
    if (!removingStore) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await deleteStore(removingStore.id, removingStore.version);
      setStores((prev) => prev.filter((s) => s.id !== removingStore.id));
      setRemovingStore(null);
    } catch (err) {
      // Keep the dialog open with the server's message — the customer can
      // retry or cancel; the tile must not disappear on a failed delete.
      setRemoveError(err instanceof ApiError ? err.message : "Failed to remove store. Please try again.");
    } finally {
      setRemoving(false);
    }
  }, [removingStore]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}>
      {loading && <p role="status">Loading your stores...</p>}

      {!loading && loadError && (
        <EmbedCard>
          <EmbedBanner tone="error">{loadError}</EmbedBanner>
          <div style={{ marginTop: t.spacing.md }}>
            <EmbedButton type="button" variant="secondary" onClick={load}>
              Retry
            </EmbedButton>
          </div>
        </EmbedCard>
      )}

      {!loading && !loadError && stores.length === 0 && (
        <p>You haven't added any stores yet. Add one to assign your products to a location.</p>
      )}

      {!loading && (
        <ManageStoresPanel
          stores={stores}
          productsByStore={productsByStore}
          ordersByStore={ordersByStore}
          onOpenOrder={onOpenOrder ?? (() => {})}
          onViewStoreOrders={onViewStoreOrders ?? (() => {})}
          onAdd={() => setAdding(true)}
          onEdit={(store) => setEditingId(store.id)}
          onRemove={(store) => {
            setRemoveError(null);
            setRemovingStore(store);
          }}
        />
      )}

      {/* Add store — StoreForm creates; the dialog owns none of the form's
          state, so a cancel posts nothing and leaves the list untouched. */}
      <EmbedDialog open={adding} onClose={() => setAdding(false)} title="Add store" size="large">
        <StoreForm
          showHeading={false}
          onBack={() => setAdding(false)}
          onSaved={(saved) => {
            setStores((prev) => [...prev, saved]);
            setAdding(false);
          }}
        />
      </EmbedDialog>

      {/* Edit store — mounts only while editingId is set, so each open seeds
          the form from the store's CURRENT version (§59 consecutive edits). */}
      <EmbedDialog
        open={editingStore != null}
        onClose={() => setEditingId(null)}
        title="Edit store"
        size="large"
      >
        {editingStore && (
          <StoreForm
            showHeading={false}
            editingStore={editingStore}
            onBack={() => setEditingId(null)}
            onSaved={(saved) => {
              // §59: adopt the response (incremented version) as the new
              // state — a refetch would be a second source of truth.
              setStores((prev) => prev.map((s) => (s.id === saved.id ? saved : s)));
              setEditingId(null);
            }}
          />
        )}
      </EmbedDialog>

      {/* Remove store — destructive, so an alertdialog: the exact copy tells
          the customer what happens to their hardware units. The server writes
          only the store row; units that were at the store keep their ledger
          rows and simply read as Unassigned in the next owned-products read. */}
      <EmbedDialog
        open={removingStore != null}
        onClose={() => {
          if (!removing) setRemovingStore(null);
        }}
        title="Remove store"
        role="alertdialog"
        size="small"
      >
        <EmbedText>
          Delete this store? Units assigned to it return to Unassigned.
          Subscriptions billed to this store keep renewing until you cancel them.
        </EmbedText>
        {removeError && (
          <div style={{ marginTop: t.spacing.md }}>
            <EmbedBanner tone="error">{removeError}</EmbedBanner>
          </div>
        )}
        <div style={{ display: "flex", gap: t.spacing.md, marginTop: t.spacing.lg, justifyContent: "flex-end" }}>
          <EmbedButton type="button" variant="secondary" onClick={() => setRemovingStore(null)} disabled={removing}>
            Cancel
          </EmbedButton>
          <EmbedButton type="button" variant="danger" loading={removing} onClick={() => void handleRemove()}>
            {removing ? "Removing..." : "Delete"}
          </EmbedButton>
        </div>
      </EmbedDialog>
    </div>
  );
}
